import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dropdown,
  Menu,
  Space,
  Tag,
  Popover,
  DatePicker,
  Input,
  InputNumber,
  Select,
} from "antd";
import {
  PlusOutlined,
  DownOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

const { RangePicker } = DatePicker;

const buildInitialFilter = (field) => {
  const base = {
    id: `${field.key}-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`,
    fieldKey: field.key,
    value: null,
  };

  if (field.type === "dateRange") {
    base.value = [null, null];
  } else if (field.type === "numberRange") {
    base.value = [null, null];
  } else if (field.type === "date") {
    base.value = null;
  } else if (field.type === "number") {
    base.value = null;
  } else if (field.type === "enumMulti") {
    base.value = [];
  } else if (field.type === "enum") {
    base.value = null;
  } else {
    // string or other
    base.value = "";
  }

  return base;
};

/**
 * Generic shared filter bar that renders:
 *  - "+ Add Filter" button with dropdown of available fields
 *  - One chip/tag per active filter
 *  - Clicking a chip opens a field-specific editor (popover)
 *
 * Props:
 *  - fields: array of field configs (see filterConfig.js)
 *  - value: controlled array of current filters (optional)
 *  - onChange: (filtersArray, normalizedFiltersObject) => void
 *  - sortFields?: array of sort field configs: [{ value, labelKey?, label? }]
 *  - sortValue?: { sort_by?: string, sort_direction?: 'asc'|'desc' } (also accepts sortBy/sortDirection)
 *  - onSortChange?: ({ sort_by, sort_direction }) => void
 */
const SharedFilterBar = ({
  fields,
  value,
  onChange,
  sortFields,
  sortValue,
  onSortChange,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(value || []);
  const [editingId, setEditingId] = useState(null);

  const getLabel = (obj, fallback = "") => {
    if (!obj) return fallback;
    if (obj.labelKey) return t(obj.labelKey);
    if (obj.label) return obj.label;
    return fallback;
  };

  const getFieldLabel = (field, fallback = "") =>
    getLabel(field, fallback || field?.key || "");

  const getOptionLabel = (opt, fallback = "") => getLabel(opt, fallback);

  const getTranslatedOptions = (field) =>
    (field.options || []).map((opt) => ({
      ...opt,
      label: getOptionLabel(opt, opt.label || String(opt.value)),
    }));

  const sortBy =
    sortValue?.sort_by !== undefined
      ? sortValue.sort_by
      : sortValue?.sortBy !== undefined
        ? sortValue.sortBy
        : undefined;

  const sortDirection =
    sortValue?.sort_direction !== undefined
      ? sortValue.sort_direction
      : sortValue?.sortDirection !== undefined
        ? sortValue.sortDirection
        : undefined;

  const sortEnabled =
    Array.isArray(sortFields) &&
    sortFields.length > 0 &&
    typeof onSortChange === "function";

  const translatedSortFields = useMemo(() => {
    if (!Array.isArray(sortFields)) return [];
    return sortFields.map((sf) => ({
      ...sf,
      label: getLabel(sf, sf.label || String(sf.value)),
    }));
  }, [sortFields, t]);

  const emitSortChange = (nextSortBy, nextSortDirection) => {
    if (!onSortChange) return;
    onSortChange({
      sort_by: nextSortBy,
      sort_direction: nextSortDirection,
    });
  };

  useEffect(() => {
    if (value) {
      setFilters(value);
    }
  }, [value]);

  const fieldsByKey = useMemo(
    () =>
      fields.reduce((acc, f) => {
        acc[f.key] = f;
        return acc;
      }, {}),
    [fields]
  );

  const availableFields = useMemo(() => {
    const usedKeys = new Set(filters.map((f) => f.fieldKey));
    return fields.filter((f) => !usedKeys.has(f.key));
  }, [fields, filters]);

  const triggerChange = (nextFilters) => {
    setFilters(nextFilters);

    if (onChange) {
      const normalized = {};

      nextFilters.forEach((f) => {
        const field = fieldsByKey[f.fieldKey];
        if (!field) return;

        if (field.type === "dateRange") {
          const raw = f.value;
          const [start, end] = Array.isArray(raw)
            ? raw
            : raw
              ? [raw, null]
              : [null, null];
          const startKey = field.startKey || `${field.key}_start`;
          const endKey = field.endKey || `${field.key}_end`;

          if (start) {
            const base = dayjs(start).startOf("day").format("YYYY-MM-DD[T]HH:mm:ss");
            normalized[startKey] = `${base}+00:00`;
          }
          if (end) {
            const base = dayjs(end).endOf("day").format("YYYY-MM-DD[T]HH:mm:ss");
            normalized[endKey] = `${base}+00:00`;
          }
        } else if (field.type === "date") {
          if (f.value) {
            const startKey = field.startKey || `${field.key}_start`;
            const endKey = field.endKey || `${field.key}_end`;
            const startBase = dayjs(f.value)
              .startOf("day")
              .format("YYYY-MM-DD[T]HH:mm:ss");
            const endBase = dayjs(f.value)
              .endOf("day")
              .format("YYYY-MM-DD[T]HH:mm:ss");
            normalized[startKey] = `${startBase}+00:00`;
            normalized[endKey] = `${endBase}+00:00`;
          }
        } else if (field.type === "numberRange") {
          const raw = f.value;
          const [min, max] = Array.isArray(raw)
            ? raw
            : raw
              ? [raw, null]
              : [null, null];
          const minKey = field.minKey || `${field.key}_min`;
          const maxKey = field.maxKey || `${field.key}_max`;

          if (min !== null && min !== undefined && min !== "") {
            normalized[minKey] = min;
          }
          if (max !== null && max !== undefined && max !== "") {
            normalized[maxKey] = max;
          }
        } else if (field.type === "number") {
          if (f.value !== null && f.value !== undefined && f.value !== "") {
            // Backend expects plain numeric values (e.g. *_min/_max), not {type,op,value}.
            normalized[field.key] = f.value;
          }
        } else if (field.type === "enumMulti") {
          if (Array.isArray(f.value) && f.value.length > 0) {
            normalized[field.key] = f.value;
          }
        } else if (field.type === "enum") {
          if (f.value !== null && f.value !== undefined && f.value !== "") {
            normalized[field.key] = f.value;
          }
        } else {
          if (f.value) {
            // Backend expects plain string values, not {type,op,value}.
            normalized[field.key] = f.value;
          }
        }
      });

      onChange(nextFilters, normalized);
    }
  };

  const handleAddField = ({ key }) => {
    const field = fieldsByKey[key];
    if (!field) return;

    const newFilter = buildInitialFilter(field);
    const next = [...filters, newFilter];
    // Only update local state, don't trigger onChange for empty filters
    // onChange will be triggered when user enters a value
    setFilters(next);
    setEditingId(newFilter.id);
  };

  const handleFilterValueChange = (id, patch) => {
    const next = filters.map((f) =>
      f.id === id
        ? {
          ...f,
          ...patch,
        }
        : f
    );
    triggerChange(next);
  };

  const handleRemove = (id) => {
    const next = filters.filter((f) => f.id !== id);
    triggerChange(next);
    if (editingId === id) {
      setEditingId(null);
    }
  };

  const menu = (
    <Menu
      onClick={handleAddField}
      style={{ minWidth: 200, paddingBlock: 4 }}
    >
      {availableFields.map((f) => (
        <Menu.Item key={f.key}>
          <span style={{ fontSize: 13 }}>{getFieldLabel(f, f.key)}</span>
        </Menu.Item>
      ))}
      {availableFields.length === 0 && (
        <Menu.Item disabled key="__none">
          <span style={{ fontSize: 12, opacity: 0.5 }}>
            {t("filters_all_fields_added")}
          </span>
        </Menu.Item>
      )}
    </Menu>
  );

  const renderFilterEditor = (filter) => {
    const field = fieldsByKey[filter.fieldKey];
    if (!field) return null;

    if (field.type === "dateRange") {
      return (
        <Space direction="vertical" size="small">
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "inherit",
            }}
          >
            {getFieldLabel(field, field.key)}
          </div>
          <RangePicker
            value={filter.value}
            onChange={(dates) =>
              handleFilterValueChange(filter.id, {
                value: dates || [null, null],
              })
            }
            allowClear
          />
        </Space>
      );
    }

    if (field.type === "date") {
      return (
        <Space direction="vertical" size="small">
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "inherit",
            }}
          >
            {getFieldLabel(field, field.key)}
          </div>
          <DatePicker
            value={filter.value}
            onChange={(date) =>
              handleFilterValueChange(filter.id, {
                value: date || null,
              })
            }
            allowClear
          />
        </Space>
      );
    }

    if (field.type === "numberRange") {
      const [min, max] = Array.isArray(filter.value)
        ? filter.value
        : [null, null];
      return (
        <Space direction="vertical" size="small">
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "inherit",
            }}
          >
            {getFieldLabel(field, field.key)}
          </div>
          <Space.Compact>
            <InputNumber
              value={min}
              onChange={(val) => {
                const [, currentMax] = Array.isArray(filter.value)
                  ? filter.value
                  : [null, null];
                handleFilterValueChange(filter.id, {
                  value: [val, currentMax],
                });
              }}
              min={field.min}
              max={field.max}
              placeholder={t('min')}
              style={{ width: 110 }}
            />
            <InputNumber
              value={max}
              onChange={(val) => {
                const [currentMin] = Array.isArray(filter.value)
                  ? filter.value
                  : [null, null];
                handleFilterValueChange(filter.id, {
                  value: [currentMin, val],
                });
              }}
              min={field.min}
              max={field.max}
              placeholder={t('max')}
              style={{ width: 110 }}
            />
          </Space.Compact>
        </Space>
      );
    }

    if (field.type === "number") {
      return (
        <Space direction="vertical" size="small">
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "inherit",
            }}
          >
            {getFieldLabel(field, field.key)}
          </div>
          <InputNumber
            value={filter.value}
            onChange={(val) => handleFilterValueChange(filter.id, { value: val })}
            min={field.min}
            max={field.max}
            style={{ width: 140 }}
          />
        </Space>
      );
    }

    if (field.type === "enumMulti") {
      return (
        <Space direction="vertical" size="small">
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "inherit",
            }}
          >
            {getFieldLabel(field, field.key)}
          </div>
          <Select
            mode="multiple"
            style={{ width: 220 }}
            value={filter.value}
            onChange={(val) =>
              handleFilterValueChange(filter.id, { value: val })
            }
            options={getTranslatedOptions(field)}
            placeholder={t("filters_select_placeholder")}
            maxTagCount="responsive"
            allowClear
          />
        </Space>
      );
    }

    if (field.type === "enum") {
      return (
        <Space direction="vertical" size="small">
          <div
            style={{
              fontWeight: 500,
              fontSize: 13,
              color: "inherit",
            }}
          >
            {getFieldLabel(field, field.key)}
          </div>
          <Select
            style={{ width: 200 }}
            value={filter.value}
            onChange={(val) =>
              handleFilterValueChange(filter.id, { value: val })
            }
            options={getTranslatedOptions(field)}
            allowClear
            showSearch={field.showSearch !== undefined ? field.showSearch : true}
            onSearch={field.onSearch}
            filterOption={
              field.filterOption !== undefined
                ? field.filterOption
                : (input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
        </Space>
      );
    }

    // default: string
    return (
      <Space direction="vertical" size="small">
        <div
          style={{
            fontWeight: 500,
            fontSize: 13,
            color: "rgba(0,0,0,0.85)",
          }}
        >
          {getFieldLabel(field, field.key)}
        </div>
        <Input
          value={filter.value}
          onChange={(e) =>
            handleFilterValueChange(filter.id, {
              value: e.target.value,
            })
          }
          style={{ width: 200 }}
        />
      </Space>
    );
  };

  const renderFilterLabel = (filter) => {
    const field = fieldsByKey[filter.fieldKey];
    if (!field) return filter.fieldKey;
    const fieldLabel = getFieldLabel(field, filter.fieldKey);

    const anyLabel = t("filters_any");
    const wrap = (valueText) => `${fieldLabel}: ${valueText}`;

    if (field.type === "dateRange") {
      const raw = filter.value;
      const [start, end] = Array.isArray(raw)
        ? raw
        : raw
          ? [raw, null]
          : [null, null];
      const formatted =
        start || end
          ? `${start ? dayjs(start).format("MMM D") : "…"} - ${end ? dayjs(end).format("MMM D") : "…"
          }`
          : anyLabel;
      return wrap(formatted);
    }

    if (field.type === "date") {
      const value = filter.value;
      const formatted = value ? dayjs(value).format("MMM D") : anyLabel;
      return wrap(formatted);
    }

    if (field.type === "numberRange") {
      const raw = filter.value;
      const [min, max] = Array.isArray(raw)
        ? raw
        : raw
          ? [raw, null]
          : [null, null];
      if (min == null && max == null) {
        return wrap(anyLabel);
      }
      const formatted = `${min != null ? min : "…"} - ${max != null ? max : "…"}`;
      return wrap(formatted);
    }

    if (field.type === "number") {
      if (filter.value == null || filter.value === "") {
        return wrap(anyLabel);
      }
      return wrap(String(filter.value));
    }

    if (field.type === "enumMulti") {
      if (!Array.isArray(filter.value) || filter.value.length === 0) {
        return wrap(anyLabel);
      }
      const labels = getTranslatedOptions(field)
        .filter((opt) => filter.value.includes(opt.value))
        .map((opt) => getOptionLabel(opt, opt.label || String(opt.value)))
        .join(", ");
      return wrap(labels);
    }

    if (field.type === "enum") {
      const opt =
        getTranslatedOptions(field).find((o) => o.value === filter.value) || {};
      return wrap(getOptionLabel(opt, opt.label) || anyLabel);
    }

    // string
    if (!filter.value) return wrap(anyLabel);
    return wrap(`"${filter.value}"`);
  };

  const getSortKeyForFieldKey = (fieldKey) =>
    fieldsByKey[fieldKey]?.sortKey || fieldKey;

  const canSortFieldKey = (fieldKey) => {
    if (!sortEnabled) return false;
    const sortKey = getSortKeyForFieldKey(fieldKey);
    return translatedSortFields.some((sf) => String(sf.value) === String(sortKey));
  };

  const normalizeSortDirection = (dir) => {
    if (!dir) return undefined;
    const d = String(dir).toLowerCase();
    if (d.startsWith("asc")) return "asc"; // supports: asc, ascend, ascending
    if (d.startsWith("desc")) return "desc"; // supports: desc, descend, descending
    return undefined;
  };

  const toggleSortForFieldKey = (fieldKey) => {
    if (!sortEnabled) return;
    const sortKey = getSortKeyForFieldKey(fieldKey);

    const isSortingThisField = String(sortBy) === String(sortKey);
    const currentDir = normalizeSortDirection(sortDirection) || "desc";

    // Behavior:
    // - If not sorting this field yet: enable ASC
    // - Otherwise: toggle ASC <-> DESC
    // (No "third click clears sort" — clearing sort should be explicit elsewhere.)
    if (!isSortingThisField) {
      emitSortChange(sortKey, "asc");
      return;
    }
    if (currentDir === "asc") {
      emitSortChange(sortKey, "desc");
      return;
    }
    emitSortChange(sortKey, "asc");
  };

  return (
    <Space wrap size={[8, 8]} style={{ width: "100%" }}>
      <Dropdown overlay={menu} trigger={["click"]}>
        <Button
          size="small"
          type="dashed"
          icon={<PlusOutlined />}
          style={{
            // Match the height of the filter "chips" (Tags) below.
            height: 30,
            paddingBlock: 4,
            borderRadius: 999,
            paddingInline: 12,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span>{t("filters_add_filter")}</span>
          <DownOutlined style={{ fontSize: 10 }} />
        </Button>
      </Dropdown>

      {filters.map((filter) => (
        <Popover
          key={filter.id}
          trigger={["click"]}
          open={editingId === filter.id}
          onOpenChange={(open) => setEditingId(open ? filter.id : null)}
          content={renderFilterEditor(filter)}
        >
          <Tag
            color="processing"
            style={{
              cursor: "pointer",
              userSelect: "none",
              borderRadius: 999,
              paddingInline: 10,
              paddingBlock: 4,
              fontSize: 13,
              background: "rgba(24, 144, 255, 0.08)",
              borderColor: "rgba(24, 144, 255, 0.45)",
            }}
            closable
            onClose={(e) => {
              e.preventDefault();
              handleRemove(filter.id);
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span>{renderFilterLabel(filter)}</span>
              {canSortFieldKey(filter.fieldKey) && (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSortForFieldKey(filter.fieldKey);
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginLeft: 2,
                    // Make active sort more visually prominent than inactive.
                    paddingInline:
                      sortBy === getSortKeyForFieldKey(filter.fieldKey) ? 4 : 0,
                    paddingBlock:
                      sortBy === getSortKeyForFieldKey(filter.fieldKey) ? 2 : 0,
                    borderRadius:
                      sortBy === getSortKeyForFieldKey(filter.fieldKey) ? 999 : 0,
                    background:
                      sortBy === getSortKeyForFieldKey(filter.fieldKey)
                        ? "rgba(24, 144, 255, 0.16)"
                        : "transparent",
                    color:
                      sortBy === getSortKeyForFieldKey(filter.fieldKey)
                        ? "rgba(24, 144, 255, 0.95)"
                        : "currentColor",
                  }}
                  title={
                    sortBy === getSortKeyForFieldKey(filter.fieldKey)
                      ? (normalizeSortDirection(sortDirection) || "desc") === "asc"
                        ? t("sort_direction_asc")
                        : t("sort_direction_desc")
                      : t("sort_direction_asc")
                  }
                >
                  {sortBy === getSortKeyForFieldKey(filter.fieldKey) ? (
                    (normalizeSortDirection(sortDirection) || "desc") === "asc" ? (
                      <ArrowUpOutlined />
                    ) : (
                      <ArrowDownOutlined />
                    )
                  ) : (
                    <ArrowDownOutlined style={{ opacity: 0.6 }} />
                  )}
                </span>
              )}
            </span>
          </Tag>
        </Popover>
      ))}


    </Space>
  );
};

export default SharedFilterBar;
